/**
 * Shared helpers for the NotificationsBell sub-tree.
 *
 * Lives in `notifications/` rather than `lib/` because nothing outside
 * the bell uses these primitives — but they're heavy enough (storage
 * helpers, icon map, time formatter) that the host component is much
 * easier to read with them factored out.
 */
import { Bell, Sparkles, Flame, ClipboardCheck, BookOpen, Target, Calendar, PartyPopper, Megaphone } from "lucide-react";

export const READ_KEY = "kiddo_notif_read_v1";
export const DISMISSED_KEY = "kiddo_notif_dismissed_v1";
export const FIRST_SEEN_KEY = "kiddo_notif_first_seen_v1";
export const ITEMS_CACHE_KEY = "kiddo_notif_items_v1";

// Icon-name → lucide component map used when rehydrating cached items.
// Cached items can't store the React component reference itself, so we
// store the key here at write time and look it up at render time.
export const ICONS = { Bell, Sparkles, Flame, ClipboardCheck, BookOpen, Target, Calendar, PartyPopper, Megaphone };

// Map an admin notification `type` → bell icon + tone. Falls back to
// Megaphone + coral for any unknown type so future backend additions
// still render gracefully.
export const ADMIN_TYPE_VISUALS = {
  activity:     { iconKey: "Calendar",       tone: "mint"  },
  assessment:   { iconKey: "ClipboardCheck", tone: "coral" },
  progress:     { iconKey: "Target",         tone: "mint"  },
  course:       { iconKey: "BookOpen",       tone: "plum"  },
  webinar:      { iconKey: "Sparkles",       tone: "plum"  },
  promotion:    { iconKey: "Flame",          tone: "coral" },
  system:       { iconKey: "Bell",           tone: "plum"  },
  announcement: { iconKey: "Megaphone",      tone: "coral" },
};

/**
 * Format a timestamp as a human-friendly relative-or-absolute string.
 * "Just now" → "12 min ago" → "Today 8:14 PM" → "Yesterday …" →
 * "Mon 14:32" (within a week) → "17 Jun" (older).
 */
export function formatNotifTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (now.toDateString() === d.toDateString()) {
    return `Today ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  }
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: "short" }) + " " +
           d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

export function readCachedItems(userId) {
  try {
    const raw = JSON.parse(localStorage.getItem(ITEMS_CACHE_KEY) || "null");
    if (!raw || raw.user_id !== userId) return [];
    return Array.isArray(raw.items) ? raw.items : [];
  } catch { return []; }
}

export function writeCachedItems(userId, items) {
  try {
    // Drop non-serialisable fields (icon component + onClick fn). The
    // `url` string is enough to rebuild `onClick` at render time.
    const lite = items.map(({ id, title, body, cta, tone, time, url, iconKey, adminId }) =>
      ({ id, title, body, cta, tone, time, url, iconKey, adminId }));
    localStorage.setItem(ITEMS_CACHE_KEY, JSON.stringify({ user_id: userId, items: lite }));
  } catch { /* quota / private mode */ }
}

export function readFirstSeen() {
  try { return JSON.parse(localStorage.getItem(FIRST_SEEN_KEY) || "{}"); } catch { return {}; }
}
export function writeFirstSeen(map) {
  try { localStorage.setItem(FIRST_SEEN_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

export function readIds(storageKey) {
  try { return new Set(JSON.parse(localStorage.getItem(storageKey) || "[]")); } catch { return new Set(); }
}
export function writeIds(storageKey, ids) {
  try { localStorage.setItem(storageKey, JSON.stringify([...ids])); } catch { /* ignore */ }
}
